import "bootstrap/dist/css/bootstrap.css";
import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import "./i18n/config";
import App from "./App";
import { authProvider } from "./services/auth";
import * as Sentry from "@sentry/react";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router";

// Sentry is initialized only for production builds. Under `npm run dev` the
// 100% performance tracing and the Session Replay (rrweb) DOM recorder ran
// constantly on top of the DataGrid, Nivo charts and real-time SignalR DOM
// updates, which was a large part of why local development felt sluggish.
if (import.meta.env.PROD) {
  Sentry.init({
    dsn:
      import.meta.env.VITE_STAGE === "CloudProduction"
        ? "https://04a5945a1ec9bea8fc7ed3220c13ee05@o4508007847493632.ingest.de.sentry.io/4508087422353488"
        : "https://c6111a3460fc7de912681a7f316f4b4f@o4508007847493632.ingest.de.sentry.io/4508087059415120",
    integrations: [
      // See docs for support of different versions of variation of react router
      // https://docs.sentry.io/platforms/javascript/guides/react/configuration/integrations/react-router/
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration(),
    ],

    // Sample 20% of transactions for performance monitoring (was 1.0, which
    // traced every fetch/XHR and navigation).
    tracesSampleRate: 0.2,

    // Capture Replay for 10% of all sessions,
    // plus for 100% of sessions with an error
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,

    environment: import.meta.env.MODE,
    release: `ra660ng-robot-hmi@${import.meta.env.VITE_UI_VERSION ?? ""}`,
  });
}

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement,
);

// MSAL v3+ requires the instance to be initialized before any other API is used.
// handleRedirectPromise + setActiveAccount run before render so Axios/SignalR token
// acquisition can use refresh-token silent renewal instead of falling back to the
// hidden-iframe flow (which triggers browser sandbox warnings).
authProvider.initialize().then(async () => {
  const redirectResult = await authProvider.handleRedirectPromise();
  const account =
    redirectResult?.account ?? authProvider.getAllAccounts()[0] ?? null;
  if (account) {
    authProvider.setActiveAccount(account);
  }

  root.render(
    <React.StrictMode>
      <App />
    </React.StrictMode>,
  );
});
